import { formatCreditsOrDash } from '@/features/ai/lib/money'
import { formatDateOrDash } from '@/lib/formatDate'
import { jobTypeLabel } from '@/features/ai/lib/jobTypeLabel'
import type { CreditsHistoryItem } from '@/features/ai/creditsApi'
import { historyRowView } from './lib/historyRow'
import { HistoryThumb } from './HistoryThumb'
import { CreditIcon } from '@/components/common/CreditIcon'
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog'
import { uiLocale, useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  item: CreditsHistoryItem | null
  onClose: () => void
}

const fullDate = () =>
  new Intl.DateTimeFormat(uiLocale(), {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })

/** Строка таблички подробностей: подпись слева, значение справа. */
const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-[8rem_minmax(0,1fr)] gap-3 sm:grid-cols-[11rem_minmax(0,1fr)] sm:gap-4 border-b border-foreground/10 px-4 py-3 last:border-0">
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="whitespace-pre-wrap break-words">{children}</dd>
  </div>
)

/** Сумма с серым цветком — тот же знак кредитов, что у строк списка. */
const Credits = ({ value }: { value: string | null | undefined }) => (
  <span className="inline-flex items-center gap-1.5">
    {formatCreditsOrDash(value)}
    <CreditIcon muted className="size-3.5" />
  </span>
)

/**
 * Подробности одного движения. Вложенный диалог поверх окна кредитов — Radix
 * это поддерживает, фокус возвращается на строку, с которой открыли. На телефоне —
 * шторка снизу (`ResponsiveDialog`), как окно покупки.
 *
 * Остаток тут назван «на момент операции» СЛОВАМИ и живёт только здесь:
 * столбцом в списке он врал бы, потому что резерв из баланса вычтен, а строк
 * резерва в списке нет.
 */
export const HistoryDetailDialog = ({ item, onClose }: Props) => {
  const m = useMessages(messages)
  return (
    <ResponsiveDialog
      open={item !== null}
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
      title={item ? historyRowView(item).label : null}
      className="max-w-xl"
    >
      {item ? (
        <>
          {/* Свой кап высоты: `DialogContent` из `@teach/ui` его не задаёт, а
              портретная картинка запуска растягивает окно почти во весь экран
              — на коротком мониторе нижние поля уезжают за вьюпорт без
              возможности прокрутить (то же, что у списка движений). Шторка
              прокручивается сама — второй полосы внутри неё не нужно. */}
          <div className="space-y-3 text-sm sm:max-h-[70vh] sm:overflow-y-auto">
            {/* Потолок высоты и подмена мёртвой ссылки — в самом компоненте:
                пропорции картинки запуска произвольные, а блоб мог уехать из
                CDN (студия сносит их, не проверяя ссылки). */}
            {/* Картинки у прогона не было — пустой баннер не рисуем. Была и
                  пропала (мёртвая ссылка) — показываем: это отличие важно. */}
            {item.detail?.imageUrl ? (
              <HistoryThumb url={item.detail.imageUrl} size="detail" />
            ) : null}
            <dl className="rounded-xl bg-foreground/5">
              {item.detail?.jobType ? (
                // Тот же словарь, что у плашки занятости и индикатора прогресса:
                // один прогон обязан называться везде одинаково. Незнакомые типы
                // (MCP, статьи) закрывает его собственный фолбэк.
                <Field label={m.fieldRun}>{jobTypeLabel(item.detail.jobType)}</Field>
              ) : null}
              {item.detail?.prompt ? (
                <Field label={m.fieldPrompt}>{item.detail.prompt}</Field>
              ) : null}
              {item.detail?.model ? <Field label={m.fieldModel}>{item.detail.model}</Field> : null}
              <Field label={m.fieldWhen}>{formatDateOrDash(item.createdAt, fullDate())}</Field>
              <Field label={m.fieldAmount}>
                <Credits value={item.amount} />
              </Field>
              <Field label={m.fieldBalanceAfter}>
                <Credits value={item.balanceAfter} />
              </Field>
            </dl>
          </div>
        </>
      ) : null}
    </ResponsiveDialog>
  )
}
